import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner, Image } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaTimesCircle, FaUpload } from 'react-icons/fa';

import { useCreateBrandMutation, useUpdateBrandMutation } from '../../slices/brandApiSlice';
import { useDeleteImageMutation } from '../../slices/utilsApiSlice';
import { compressAndUploadImage } from '../../utils/compressAndUploadImage';
import ImageCropper from '../ui/ImageCropper';

const defaultForm = {
    name: '',
    description: '',
    image: '',
    isActive: true,
};

const BrandFormModal = ({ show, handleClose, initialData = null, onSuccess }) => {
    const [createBrand, { isLoading: creating, error: createError }] = useCreateBrandMutation();
    const [updateBrand, { isLoading: updating, error: updateError }] = useUpdateBrandMutation();
    const [deleteImage] = useDeleteImageMutation();

    const [formData, setFormData] = useState(defaultForm);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showCropper, setShowCropper] = useState(false);
    const [rawImageSrc, setRawImageSrc] = useState(null);
    const fileInputRef = useRef(null);

    const isEditing = Boolean(initialData?._id);
    const disabled = isSubmitting || creating || updating || isUploading;

    useEffect(() => {
        if (!show) return;
        setFormData({
            name: initialData?.name || '',
            description: initialData?.description || '',
            image: initialData?.image || '',
            isActive: initialData?.isActive !== undefined ? Boolean(initialData.isActive) : true,
        });
    }, [show, initialData]);

    useEffect(() => {
        if (show) return;
        setShowCropper(false);
        setRawImageSrc(null);
    }, [show]);

    const onFieldChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            setRawImageSrc(reader.result);
            setShowCropper(true);
        };
        reader.readAsDataURL(file);

        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleCropComplete = async (croppedImageFile) => {
        const previousImage = formData.image;
        setIsUploading(true);

        try {
            const result = await compressAndUploadImage(croppedImageFile, 'image');
            const urls = result?.images || (Array.isArray(result?.image) ? result.image : [result?.image]);
            const nextImage = urls?.[0] || result?.[0];

            if (!nextImage) {
                toast.error('No se recibió URL de imagen.');
                return;
            }

            if (previousImage && previousImage !== nextImage) {
                await deleteImage({ imageName: previousImage }).unwrap();
            }

            onFieldChange('image', nextImage);
            toast.success(result?.message || 'Imagen subida correctamente.');
        } catch (err) {
            toast.error(err?.data?.message || err?.message || 'No se pudo subir la imagen.');
        } finally {
            setIsUploading(false);
            setShowCropper(false);
            setRawImageSrc(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            name: String(formData.name || '').trim(),
            description: String(formData.description || '').trim(),
            image: String(formData.image || '').trim(),
            isActive: Boolean(formData.isActive),
        };

        if (!payload.name) {
            toast.error('El nombre de la marca es obligatorio.');
            return;
        }

        setIsSubmitting(true);
        try {
            if (isEditing) {
                await updateBrand({ id: initialData._id, ...payload }).unwrap();
                toast.success('Marca actualizada correctamente.');
            } else {
                await createBrand(payload).unwrap();
                toast.success('Marca creada correctamente.');
            }
            if (onSuccess) await onSuccess();
            handleClose();
        } catch (err) {
            toast.error(err?.data?.message || err?.error || 'No se pudo guardar la marca.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const apiError =
        createError?.data?.message ||
        updateError?.data?.message ||
        createError?.error ||
        updateError?.error;

    return (
        <>
        <Modal show={show} onHide={handleClose} centered size="md" className="brand-form-modal">
            <Modal.Header closeButton style={{ borderBottomColor: '#f2d6cb' }}>
                <Modal.Title style={{ color: '#b5430f' }}>
                    {isEditing ? 'Editar marca' : 'Nueva marca'}
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {apiError ? <Alert variant="danger">{apiError}</Alert> : null}

                    <Row className="g-3">
                        {/* Imagen preview + upload */}
                        <Col xs={12} className="d-flex flex-column align-items-center gap-2">
                            {formData.image ? (
                                <Image
                                    src={formData.image}
                                    alt="Logo de marca"
                                    thumbnail
                                    style={{ maxHeight: '120px', objectFit: 'contain' }}
                                />
                            ) : (
                                <div
                                    className="d-flex align-items-center justify-content-center rounded border text-muted"
                                    style={{ width: '120px', height: '80px', backgroundColor: '#fdf5f2' }}
                                >
                                    Sin imagen
                                </div>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="d-none"
                                id="brand-image-upload"
                                onChange={handleImageChange}
                                disabled={disabled}
                            />
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={disabled}
                                className="d-inline-flex align-items-center gap-2"
                            >
                                {isUploading ? (
                                    <><Spinner animation="border" size="sm" /> Subiendo...</>
                                ) : (
                                    <><FaUpload /> {formData.image ? 'Cambiar imagen' : 'Subir imagen'}</>
                                )}
                            </Button>
                        </Col>

                        {/* Nombre */}
                        <Col xs={12}>
                            <Form.Group controlId="brand-name">
                                <Form.Label column={false} className="fw-semibold">
                                    Nombre <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => onFieldChange('name', e.target.value)}
                                    placeholder="Ej: Samsung"
                                    required
                                    maxLength={80}
                                    disabled={disabled}
                                />
                            </Form.Group>
                        </Col>

                        {/* Descripción */}
                        <Col xs={12}>
                            <Form.Group controlId="brand-description">
                                <Form.Label column={false} className="fw-semibold">Descripción</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={formData.description}
                                    onChange={(e) => onFieldChange('description', e.target.value)}
                                    placeholder="Descripción breve de la marca..."
                                    maxLength={500}
                                    disabled={disabled}
                                />
                            </Form.Group>
                        </Col>

                        {/* Estado activo */}
                        <Col xs={12}>
                            <Form.Group controlId="brand-is-active">
                                <div
                                    className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 rounded-3 p-3"
                                    style={{ backgroundColor: '#fff5f2', border: '1px solid #f2d6cb' }}
                                >
                                    <div>
                                        <Form.Label column={false} className="fw-semibold mb-1">Estado</Form.Label>
                                        <Form.Text className="d-block text-muted">
                                            Las marcas inactivas no son visibles en la tienda.
                                        </Form.Text>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Check
                                            id="brand-is-active-switch"
                                            type="switch"
                                            label={formData.isActive ? 'Activa' : 'Inactiva'}
                                            checked={Boolean(formData.isActive)}
                                            onChange={(e) => onFieldChange('isActive', e.target.checked)}
                                            disabled={disabled}
                                            className="mb-0 fw-semibold"
                                        />
                                        {formData.isActive ? (
                                            <FaCheckCircle className="text-success" />
                                        ) : (
                                            <FaTimesCircle className="text-secondary" />
                                        )}
                                    </div>
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>

                <Modal.Footer className="d-flex justify-content-between" style={{ borderTopColor: '#f2d6cb' }}>
                    <Button variant="outline-secondary" onClick={handleClose} disabled={disabled}>
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        disabled={disabled}
                        style={{ backgroundColor: '#b5430f', borderColor: '#b5430f' }}
                    >
                        {disabled && !isUploading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Guardando...
                            </>
                        ) : isEditing ? (
                            'Actualizar marca'
                        ) : (
                            'Crear marca'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
        <Modal
            show={showCropper}
            onHide={() => {
                setShowCropper(false);
                setRawImageSrc(null);
            }}
            size="lg"
            centered
            fullscreen="sm-down"
        >
            <Modal.Header closeButton>
                <Modal.Title>Recortar imagen</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {rawImageSrc ? (
                    <ImageCropper
                        imageSrc={rawImageSrc}
                        onCropComplete={handleCropComplete}
                        onCancel={() => {
                            setShowCropper(false);
                            setRawImageSrc(null);
                        }}
                    />
                ) : null}
            </Modal.Body>
        </Modal>
        </>
    );
};

export default BrandFormModal;

